import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { X, Upload } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sample football images for "upload" simulation
const sampleImages = [
    "https://images.unsplash.com/photo-1574629810360-7efbbe195018?w=800",
    "https://images.unsplash.com/photo-1553778263-73a83bab9b0c?w=800",
    "https://images.unsplash.com/photo-1579952363873-27f3bade9f55?w=800",
    "https://images.unsplash.com/photo-1606925797300-0b35e9d1794e?w=800",
    "https://images.unsplash.com/photo-1431324155629-1a6deb1dec8d?w=800",
    "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=800",
];

const CreatePostModal = ({ open, onClose }) => {
    const [caption, setCaption] = useState('');
    const [selectedImage, setSelectedImage] = useState(null);
    const [positionTag, setPositionTag] = useState('');
    const [level, setLevel] = useState('');
    const [loading, setLoading] = useState(false);
    const [showImagePicker, setShowImagePicker] = useState(false);

    const positions = ['Forward', 'Midfielder', 'Defender', 'Goalkeeper'];
    const levels = ['Amateur', 'Semi-Pro', 'Professional', 'Youth'];

    // Reset form when modal closes
    useEffect(() => {
        if (!open) {
            setCaption('');
            setSelectedImage(null);
            setPositionTag('');
            setLevel('');
            setShowImagePicker(false);
        }
    }, [open]);

    const handleSubmit = async () => {
        if (!selectedImage) {
            toast.error('Please select an image');
            return;
        }

        if (!caption.trim()) {
            toast.error('Please add a caption');
            return;
        }

        setLoading(true);
        try {
            await axios.post(`${API}/posts`, {
                caption: caption.trim(),
                media_url: selectedImage,
                media_type: 'image',
                position_tag: positionTag || null,
                level: level || null
            });

            toast.success('Post created successfully!');
            onClose();
            // Trigger feed refresh
            window.dispatchEvent(new Event('post-created'));
        } catch (error) {
            console.error('Error creating post:', error);
            toast.error('Failed to create post');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()} modal={true}>
            <DialogContent className="bg-card-bg border-border max-w-md mx-auto" data-testid="create-post-modal">
                <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-white">Create Post</DialogTitle>
                </DialogHeader>

                <div className="space-y-4 mt-4">
                    {/* Image Selection */}
                    {!selectedImage ? (
                        <button
                            onClick={() => setShowImagePicker(true)}
                            className="w-full h-48 border-2 border-dashed border-white/20 rounded-xl flex flex-col items-center justify-center gap-2 hover:border-primary transition-colors"
                            data-testid="upload-image-button"
                        >
                            <Upload className="w-10 h-10 text-white/40" />
                            <span className="text-white/60">Tap to select image</span>
                        </button>
                    ) : (
                        <div className="relative">
                            <img
                                src={selectedImage}
                                alt="Selected"
                                className="w-full h-48 object-cover rounded-xl"
                            />
                            <button
                                onClick={() => setSelectedImage(null)}
                                className="absolute top-2 right-2 w-8 h-8 bg-black/60 rounded-full flex items-center justify-center"
                                data-testid="remove-image-button"
                            >
                                <X className="w-4 h-4 text-white" />
                            </button>
                        </div>
                    )}

                    {/* Image Picker Grid */}
                    {showImagePicker && !selectedImage && (
                        <div className="grid grid-cols-3 gap-2 p-2 bg-surface rounded-xl max-h-48 overflow-y-auto">
                            {sampleImages.map((img, index) => (
                                <button
                                    key={index}
                                    onClick={() => {
                                        setSelectedImage(img);
                                        setShowImagePicker(false);
                                    }}
                                    className="aspect-square rounded-lg overflow-hidden hover:ring-2 ring-primary transition-all"
                                    data-testid={`sample-image-${index}`}
                                >
                                    <img src={img} alt={`Sample ${index + 1}`} className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Caption */}
                    <Textarea
                        placeholder="Write a caption..."
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        className="bg-surface border-border text-white placeholder:text-white/40 min-h-[100px] resize-none"
                        data-testid="post-caption-input"
                    />

                    {/* Tags */}
                    <div className="grid grid-cols-2 gap-3">
                        <Select value={positionTag} onValueChange={setPositionTag}>
                            <SelectTrigger className="bg-surface border-border text-white" data-testid="position-select">
                                <SelectValue placeholder="Position" />
                            </SelectTrigger>
                            <SelectContent className="bg-card-bg border-border">
                                {positions.map((pos) => (
                                    <SelectItem key={pos} value={pos} className="text-white hover:bg-primary">
                                        {pos}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={level} onValueChange={setLevel}>
                            <SelectTrigger className="bg-surface border-border text-white" data-testid="level-select">
                                <SelectValue placeholder="Level" />
                            </SelectTrigger>
                            <SelectContent className="bg-card-bg border-border">
                                {levels.map((lvl) => (
                                    <SelectItem key={lvl} value={lvl} className="text-white hover:bg-primary">
                                        {lvl}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    {/* Submit Button */}
                    <Button
                        onClick={handleSubmit}
                        disabled={loading || !selectedImage || !caption.trim()}
                        className="w-full bg-primary hover:bg-primary-hover text-white font-semibold py-3 rounded-full"
                        data-testid="submit-post-button"
                    >
                        {loading ? 'Posting...' : 'Share Post'}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default CreatePostModal;
